import React, { useEffect, useMemo, useState } from 'react';

import PrivateLayout from "layouts/private";
import { fetchTransaction, updateTransaction } from "apiRequest/transaction";
import { useNavigate, useParams } from 'react-router-dom';
import isEmpty from 'lodash.isempty';
import updateTransactionValidation from 'formValidations/updateTransaction';
import FormContainer from 'components/FormContainer';
import useToken from 'hooks/useToken';
import { useMutation, useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { defaultError } from 'utils/message';
import TransactionForm from 'components/forms/TransactionForm';
import { fetchAccountsAll } from 'apiRequest/account';
import flatMap from 'lodash.flatmap';
import sumBy from 'lodash.sumby';

export default function TransactionUpdate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useToken();

  // bought logic start
  const [boughtCurrency, setBoughtCurrency] = useState();
  const {
    isError: isErrorBoughtAccounts,
    data: boughtAccounts,
    error: errorBoughtAccounts,
  } = useQuery(
    ["account-fetchAccountsAll", boughtCurrency],
    () => fetchAccountsAll({
      token, params: {
        filters: { currency: boughtCurrency, hasCapitals: true },
        partials: ['capitals.figure']
      }
    }),
    { enabled: !!boughtCurrency },
  );

  if (isErrorBoughtAccounts) {
    toast.error(errorBoughtAccounts.message || defaultError);
    toast.error('contact admin');
    console.error(errorBoughtAccounts);
  }

  const boughtAccountOptions = useMemo(() => (
    boughtAccounts?.data.data.map(({ id, name }) => ({ value: id, label: name }))
  ), [boughtAccounts]);

  const boughtCapitals = useMemo(() => (
    flatMap(boughtAccounts?.data.data.map(({ capitals }) => (capitals))).map(({
      id: capitalId,
      account_id: accountId,
      quantity,
      figure
    }) => ({
      accountId,
      quantity,
      capitalId,
      figureId: figure.id,
      worth: figure.worth,
      currency: figure.currency,
      figureCode: figure.code,
    }))
  ), [boughtAccounts]);
  // bought logic end

  // sold logic start
  const [soldCurrency, setSoldCurrency] = useState();
  const {
    isError: isErrorSoldAccounts,
    data: soldAccounts,
    error: errorSoldAccounts,
  } = useQuery(
    ["account-fetchAccountsAll", soldCurrency],
    () => fetchAccountsAll({
      token, params: {
        filters: { currency: soldCurrency, hasCapitals: true },
        partials: ['capitals.figure']
      }
    }),
    { enabled: !!soldCurrency },
  );

  if (isErrorSoldAccounts) {
    toast.error(errorSoldAccounts.message || defaultError);
    toast.error('contact admin');
    console.error(errorSoldAccounts);
  }

  const soldAccountOptions = useMemo(() => (
    soldAccounts?.data.data.map(({ id, name }) => ({ value: id, label: name }))
  ), [soldAccounts]);

  const soldCapitals = useMemo(() => (
    flatMap(soldAccounts?.data.data.map(({ capitals }) => (capitals))).map(({
      id: capitalId,
      account_id: accountId,
      quantity,
      figure,
    }) => ({
      accountId,
      quantity,
      capitalId,
      figureId: figure.id,
      worth: figure.worth,
      currency: figure.currency,
      figureCode: figure.code,
    }))
  ), [soldAccounts]);
  // sold logic end

  const handleOnCurrencyChange = ({ value, type }) => {
    if (type === "bought") {
      setBoughtCurrency(value);
    }

    if (type === "sold") {
      setSoldCurrency(value);
    }
  };

  const getCapitalOptions = ({ accountId, type }) => {
    if (!accountId || !type) return [];

    let capitals = undefined;
    if (type === 'bought') {
      capitals = boughtCapitals;
    }

    if (type === "sold") {
      capitals = soldCapitals;
    }

    return capitals?.filter((capital) => (capital.accountId === accountId))
      ?.map(({ capitalId, figureCode }) => ({ value: capitalId, label: figureCode }));
  };

  const getItemCalculation = ({ capitalId, quantity, type }) => {
    if (!capitalId || !quantity || !type) return 0;

    let capitals = undefined;
    if (type === 'bought') {
      capitals = boughtCapitals;
    }

    if (type === "sold") {
      capitals = soldCapitals;
    }

    const capital = capitals.filter((capital) => (capital.capitalId === capitalId))[0];
    if (!capital) return 0;

    return `${capital.worth * quantity}`;
  };

  const getTotalCalculation = ({ type, values }) => {
    if (!type) return 0;

    let subTransactions = undefined;
    let capitals = undefined;
    if (type === 'bought') {
      capitals = boughtCapitals;
      subTransactions = values.sub_transactions
        ?.map(({ bought_capital_id: capitalId, bought_quantity: quantity }) => ({ capitalId, quantity }));
    }
    if (type === "sold") {
      capitals = soldCapitals;
      subTransactions = values.sub_transactions
        ?.map(({ sold_capital_id: capitalId, sold_quantity: quantity }) => ({ capitalId, quantity }));
    }

    subTransactions = subTransactions?.filter(({ quantity, capitalId }) => (quantity > 0 && capitalId));

    if (!subTransactions) {
      return 0;
    }

    subTransactions = subTransactions?.map((subTransaction) => {
      const capital = capitals.filter((capital) => (capital.capitalId === subTransaction.capitalId))[0];
      return {
        quantity: subTransaction.quantity,
        worth: capital ? capital.worth : 0
      };
    });

    return sumBy(subTransactions, ({ worth, quantity }) => (worth * quantity));
  };

  const { data: transaction, isLoading, isError: errorTransactions, error: isErrorTransaction } = useQuery("transaction-fetchTransaction", () => {
    return fetchTransaction({ token, id, params: { partials: ['dealer_payment'] } });
  });

  const mutation = useMutation(data => {
    return updateTransaction({ token, data, id });
  });

  const handleOnSubmit = async ({ values: data }) => {
    const submitData = {
      ...data
    };

    mutation.mutate(submitData, {
      onSuccess: () => {
        navigate(`/transactions/${id}`, { replace: true });
      },
      onError: (error) => {
        if (error.response?.status !== 422) {
          toast.error(error.message || defaultError);
          console.error(error);
        }
      }
    });
  };

  const initialValues = useMemo(() => {
    const subTransactions = transaction?.data.data?.sub_transactions.map((subTransaction) => {
      return {
        bought_account_id: subTransaction.bought_capital.account_id,
        bought_capital_id: subTransaction.bought_capital.id,
        bought_quantity: subTransaction.bought_quantity,
        sold_account_id: subTransaction.sold_capital.account_id,
        sold_capital_id: subTransaction.sold_capital.id,
        sold_quantity: subTransaction.sold_quantity,
      };
    });
    return {
      ...transaction?.data.data,
      sub_transactions: subTransactions
    };
  }, [transaction?.data.data]);

  useEffect(() => {
    if (transaction?.data.data) {
      setBoughtCurrency(transaction?.data.data.bought_currency);
      setSoldCurrency(transaction?.data.data.sold_currency);
    }
  }, [transaction]);

  return (
    <PrivateLayout
      loading={isLoading}
      error={errorTransactions}
      isError={isErrorTransaction}>
      {(!isEmpty(transaction?.data.data)) &&
        <FormContainer
          title="Edit Transaction"
          subheader={transaction?.data.data.name}
          onSubmit={handleOnSubmit}
          validationSchema={updateTransactionValidation}
          serverError={mutation.error}
          initialValues={initialValues}
        >
          <TransactionForm
            isUpdate={true}
            loading={mutation.isLoading}
            boughtAccountOptions={boughtAccountOptions}
            soldAccountOptions={soldAccountOptions}
            getCapitalOptions={getCapitalOptions}
            getItemCalculation={getItemCalculation}
            getTotalCalculation={getTotalCalculation}
            onCurrencyChange={handleOnCurrencyChange}
            boughtCapitals={boughtCapitals}
            soldCapitals={soldCapitals}
          />
        </FormContainer>
      }
    </PrivateLayout >
  );
}
